import { forwardRef, useId } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cn } from '../../../../lib/utils.js';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Optional label rendered above the textarea. */
  label?: string;
  /** Helper or validation message shown below the textarea. */
  helperText?: string;
  /** Marks the textarea as invalid and applies error styles. */
  error?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, label, helperText, error = false, id, disabled, rows = 4, ...props },
  ref
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  return (
    <label className="flex w-full flex-col gap-1.5 text-body-14 text-ig-text-primary" htmlFor={textareaId}>
      {label && <span className="text-label-12 font-medium uppercase tracking-wide text-ig-text-secondary">{label}</span>}
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={cn(
          'w-full rounded-md border bg-ig-surface-input px-3 py-2 text-body-14 text-ig-text-primary transition duration-fast ease-ig',
          'placeholder:text-ig-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ig-accent/60',
          error ? 'border-ig-danger' : 'border-ig-border',
          disabled && 'cursor-not-allowed opacity-60',
          className
        )}
        {...props}
      />
      {helperText && <span className={cn('text-label-12', error ? 'text-ig-danger' : 'text-ig-text-secondary')}>{helperText}</span>}
    </label>
  );
});
